import {
  docMmToPx,
  type Guide,
  layoutGridGeometry,
  pageSize,
  type SceneDocument,
} from "@cva/shared";
import { Crosshair, Lock, LockOpen, Trash2 } from "lucide-react";
import {
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { create } from "zustand";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { Field, Input } from "../components/ui/input";
import { selectionRect } from "./commands";
import { collectTargets, snapValue } from "./snapping";
import { type EditorStore, pagePan, useEditor, useEditorApi } from "./store";
import { Segmented } from "./ui";
import {
  addGuide,
  clearGuides,
  formatLength,
  formatTick,
  layoutUnits,
  moveGuide,
  removeGuide,
  roundGuidePosition,
  rulerTicks,
  useViewPrefs,
} from "./view";

/**
 * Rulers along the top and left edge of the canvas, the document's guides
 * and the layout grid overlay (docs/04-dokumentformat.md). Editor aids only:
 * the renderer never draws them.
 */

/** Thickness of a ruler in screen pixels. */
export const RULER_SIZE = 20;
const SNAP_PX = 6;
/** Hit area of a guide line in screen pixels. */
const GUIDE_HIT_PX = 7;

export const GUIDE_COLOR = "#059669";
export const GRID_COLOR = "rgb(139 92 246 / 0.7)";
const GRID_FILL = "rgb(139 92 246 / 0.07)";

/** Transient UI of the guides: the drag readout, the position dialog, the context menu. */
const useGuideUi = create<{
  readout: {
    id: string;
    axis: Guide["axis"];
    position: number;
    /** Pointer in viewport coordinates. */
    x: number;
    y: number;
    /** Released here, the guide is removed. */
    remove?: boolean;
  } | null;
  dialog: { id: string } | { axis: Guide["axis"]; position: number } | null;
  menu: { id: string; x: number; y: number } | null;
}>()(() => ({ readout: null, dialog: null, menu: null }));

/** Opens the dialog for a new guide at an exact position. */
export function requestNewGuide(axis: Guide["axis"] = "x", position = 0) {
  useGuideUi.setState({ dialog: { axis, position } });
}

/** Rulers and guides are part of the design editor (not of videos). */
export function useLayoutAids(): boolean {
  return useEditor((s) => s.doc.kind === "design");
}

function useViewportSize(viewportRef: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    const update = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [viewportRef]);
  return size;
}

// ---------------------------------------------------------------------------
// Dragging guides (new ones out of a ruler, existing ones on the canvas)
// ---------------------------------------------------------------------------

interface GuideDrag {
  axis: Guide["axis"];
  /** Guide being moved; null while a new one has not left the ruler yet. */
  id: string | null;
  /** The guide existed before the drag (moving, not creating). */
  existed: boolean;
  /** The pointer is over the ruler: releasing removes the guide. */
  overRuler: boolean;
  /** Pointer position at the start; existing guides move after a few pixels only. */
  start: { x: number; y: number };
  moved: boolean;
  targets: ReturnType<typeof collectTargets>;
}

/**
 * Pointer handlers for an element that starts a guide drag. The element keeps
 * the pointer capture, so the whole drag arrives here; a guide released over
 * its ruler (or outside the canvas when the rulers are hidden) is removed.
 * One undo step per drag.
 */
function useGuideDragHandlers(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  start: () => { axis: Guide["axis"]; id: string | null } | null,
) {
  const store = useEditorApi();
  const drag = useRef<GuideDrag | null>(null);

  const place = (event: ReactPointerEvent, current: GuideDrag) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    if (!current.moved) {
      if (Math.hypot(event.clientX - current.start.x, event.clientY - current.start.y) < 3) return;
      current.moved = true;
    }
    const state = store.getState();
    const rect = viewport.getBoundingClientRect();
    const local = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const edge = useViewPrefs.getState().rulers ? RULER_SIZE : 0;
    current.overRuler = current.axis === "x" ? local.x < edge : local.y < edge;
    if (current.overRuler) {
      useGuideUi.setState({
        readout: current.id
          ? {
              id: current.id,
              axis: current.axis,
              position: 0,
              x: local.x,
              y: local.y,
              remove: true,
            }
          : null,
      });
      return;
    }
    const screen = current.axis === "x" ? local.x : local.y;
    const origin = pagePan(state);
    const offset = current.axis === "x" ? origin.x : origin.y;
    let position = (screen - offset) / state.zoom;
    const snapped =
      event.metaKey || event.ctrlKey
        ? null
        : snapValue(
            position,
            current.axis === "x" ? current.targets.x : current.targets.y,
            SNAP_PX / state.zoom,
            current.axis,
            { from: 0, to: 0 },
          );
    position =
      snapped && snapped.guides.length > 0
        ? snapped.value
        : roundGuidePosition(state.doc, position);
    if (current.id && state.doc.guides?.some((guide) => guide.id === current.id)) {
      moveGuide(store, current.id, position);
    } else {
      current.id = addGuide(store, current.axis, position);
    }
    if (!current.id) return;
    useGuideUi.setState({
      readout: { id: current.id, axis: current.axis, position, x: local.x, y: local.y },
    });
  };

  const finish = () => {
    const current = drag.current;
    drag.current = null;
    useGuideUi.setState({ readout: null });
    if (!current) return;
    const state = store.getState();
    if (current.overRuler && current.id) {
      // A new guide that went back into the ruler: as if nothing happened.
      if (!current.existed) return state.cancelGesture();
      removeGuide(store, current.id);
    }
    if (!current.id) state.cancelGesture();
    else state.endGesture();
  };

  return {
    onPointerDown(event: ReactPointerEvent<HTMLElement>) {
      event.stopPropagation();
      if (event.button !== 0) return;
      const state = store.getState();
      const begin = start();
      if (!begin || state.readOnly) return;
      // No preventDefault: it would swallow the double click (position dialog).
      event.currentTarget.setPointerCapture(event.pointerId);
      state.beginGesture();
      const prefs = useViewPrefs.getState();
      const print = state.doc.format.print;
      drag.current = {
        ...begin,
        existed: begin.id !== null,
        overRuler: false,
        start: { x: event.clientX, y: event.clientY },
        // Pulling out of a ruler follows the pointer right away.
        moved: begin.id === null,
        // Guides snap to the page, the elements and the grid (not to other guides).
        targets: collectTargets(
          state.doc,
          state.pageIndex,
          new Set(),
          print
            ? {
                bleedPx: docMmToPx(state.doc, print.bleedMm),
                safePx: docMmToPx(state.doc, print.safeMm),
              }
            : null,
          { guides: false, grid: prefs.grid },
        ),
      };
    },
    onPointerMove(event: ReactPointerEvent<HTMLElement>) {
      if (!drag.current) return;
      event.stopPropagation();
      place(event, drag.current);
    },
    onPointerUp(event: ReactPointerEvent<HTMLElement>) {
      event.stopPropagation();
      finish();
    },
    onPointerCancel(event: ReactPointerEvent<HTMLElement>) {
      event.stopPropagation();
      finish();
    },
  };
}

// ---------------------------------------------------------------------------
// Rulers
// ---------------------------------------------------------------------------

function drawRuler(
  canvas: HTMLCanvasElement,
  options: {
    axis: "x" | "y";
    length: number;
    /** Screen position of the document's 0 within the ruler. */
    origin: number;
    zoom: number;
    pxPerUnit: number;
    pageLength: number;
    selection: { from: number; to: number } | null;
    cursor: number | null;
  },
) {
  const ratio = window.devicePixelRatio || 1;
  const width = options.axis === "x" ? options.length : RULER_SIZE;
  const height = options.axis === "x" ? RULER_SIZE : options.length;
  if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
  }
  const context = canvas.getContext("2d");
  if (!context) return;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  // Work in "along / across" coordinates: along the axis, across its thickness.
  if (options.axis === "y") {
    context.translate(RULER_SIZE, 0);
    context.rotate(Math.PI / 2);
  }
  const { origin, zoom, length } = options;
  const toScreen = (px: number) => origin + px * zoom;
  context.fillStyle = "#f4f4f5";
  context.fillRect(0, 0, length, RULER_SIZE);
  // The page (trim box) lighter than the pasteboard.
  const pageFrom = Math.max(0, toScreen(0));
  const pageTo = Math.min(length, toScreen(options.pageLength));
  if (pageTo > pageFrom) {
    context.fillStyle = "#ffffff";
    context.fillRect(pageFrom, 0, pageTo - pageFrom, RULER_SIZE);
  }
  if (options.selection) {
    const from = toScreen(options.selection.from);
    const to = toScreen(options.selection.to);
    context.fillStyle = "rgb(99 102 241 / 0.18)";
    context.fillRect(from, 0, Math.max(1, to - from), RULER_SIZE);
    context.fillStyle = "rgb(79 70 229 / 0.9)";
    context.fillRect(Math.round(from), 0, 1, RULER_SIZE);
    context.fillRect(Math.round(to) - 1, 0, 1, RULER_SIZE);
  }
  const { step, minor } = rulerTicks(options.pxPerUnit, zoom);
  const unitScreen = options.pxPerUnit * zoom;
  const firstUnit = Math.floor(-origin / unitScreen / step) * step;
  const lastUnit = (length - origin) / unitScreen;
  context.fillStyle = "#a1a1aa";
  context.font = "9px Inter, system-ui, sans-serif";
  context.textBaseline = "top";
  for (let unit = firstUnit; unit <= lastUnit + step; unit += step) {
    for (let index = 0; index < minor; index++) {
      const value = unit + (step / minor) * index;
      const x = Math.round(origin + value * unitScreen) + 0.5;
      if (x < -1 || x > length + 1) continue;
      const tick =
        index === 0
          ? RULER_SIZE
          : minor % 2 === 0 && index === minor / 2
            ? RULER_SIZE * 0.4
            : RULER_SIZE * 0.25;
      context.fillRect(x - 0.5, RULER_SIZE - tick, 1, tick);
    }
    const x = Math.round(origin + unit * unitScreen);
    if (x >= -40 && x <= length + 40) {
      context.fillStyle = "#71717a";
      context.fillText(formatTick(unit, step), x + 3, 2);
      context.fillStyle = "#a1a1aa";
    }
  }
  if (options.cursor !== null) {
    context.fillStyle = "#e11d48";
    context.fillRect(Math.round(options.cursor), 0, 1, RULER_SIZE);
  }
  context.fillStyle = "#d4d4d8";
  context.fillRect(0, RULER_SIZE - 1, length, 1);
}

/** Top and left ruler, in screen space over the canvas. Pull a guide out of one. */
export function Rulers({ viewportRef }: { viewportRef: React.RefObject<HTMLDivElement | null> }) {
  const show = useViewPrefs((s) => s.rulers);
  const aids = useLayoutAids();
  const readout = useGuideUi((s) => s.readout);
  const doc = useEditor((s) => s.doc);
  const size = useViewportSize(viewportRef);
  if (!aids) return null;
  return (
    <>
      {show && size.width > 0 ? (
        <>
          <Ruler axis="x" viewportRef={viewportRef} length={size.width - RULER_SIZE} />
          <Ruler axis="y" viewportRef={viewportRef} length={size.height - RULER_SIZE} />
          {/* biome-ignore lint/a11y/noStaticElementInteractions: keeps clicks on the corner away from the canvas */}
          <div
            className="absolute top-0 left-0 z-10 flex items-center justify-center border-r border-b border-zinc-300 bg-zinc-100 text-[9px] text-zinc-500"
            style={{ width: RULER_SIZE, height: RULER_SIZE }}
            title="Maßeinheit der Lineale – Nullpunkt ist die obere linke Ecke des Endformats"
            onPointerDown={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            {layoutUnits(doc).unit}
          </div>
        </>
      ) : null}
      {readout ? (
        <div
          className="pointer-events-none absolute z-20 rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap text-white tabular-nums shadow"
          style={{ left: readout.x + 12, top: readout.y + 12 }}
        >
          {readout.remove
            ? "Loslassen zum Löschen"
            : `${readout.axis === "x" ? "X" : "Y"} ${formatLength(doc, readout.position)}`}
        </div>
      ) : null}
    </>
  );
}

function Ruler({
  axis,
  length,
  viewportRef,
}: {
  axis: "x" | "y";
  length: number;
  viewportRef: React.RefObject<HTMLDivElement | null>;
}) {
  const store = useEditorApi();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readOnly = useEditor((s) => s.readOnly);
  const cursor = useRef<number | null>(null);
  const handlers = useGuideDragHandlers(viewportRef, () =>
    // A ruler along the top pulls out horizontal guides (a position on the y axis).
    ({ axis: axis === "x" ? "y" : "x", id: null }),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !viewport || length <= 0) return;
    let frame = 0;
    const draw = () => {
      frame = 0;
      const state = store.getState();
      const { doc, zoom, selection } = state;
      const pan = pagePan(state);
      const pageDim = pageSize(doc, doc.pages[state.pageIndex]);
      const print = doc.format.print;
      const rect = selectionRect(doc, selection);
      drawRuler(canvas, {
        axis,
        length,
        origin: (axis === "x" ? pan.x : pan.y) - RULER_SIZE,
        zoom,
        pxPerUnit: print ? doc.format.width / print.widthMm : 1,
        pageLength: axis === "x" ? pageDim.width : pageDim.height,
        selection: rect
          ? axis === "x"
            ? { from: rect.x, to: rect.x + rect.width }
            : { from: rect.y, to: rect.y + rect.height }
          : null,
        cursor: cursor.current,
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(draw);
    };
    draw();
    const unsubscribe = store.subscribe((state, previous) => {
      if (
        state.zoom !== previous.zoom ||
        state.pan !== previous.pan ||
        state.pageIndex !== previous.pageIndex ||
        state.selection !== previous.selection ||
        state.doc !== previous.doc
      ) {
        schedule();
      }
    });
    const onMove = (event: PointerEvent) => {
      const bounds = viewport.getBoundingClientRect();
      cursor.current =
        (axis === "x" ? event.clientX - bounds.left : event.clientY - bounds.top) - RULER_SIZE;
      schedule();
    };
    const onLeave = () => {
      cursor.current = null;
      schedule();
    };
    viewport.addEventListener("pointermove", onMove);
    viewport.addEventListener("pointerleave", onLeave);
    return () => {
      unsubscribe();
      viewport.removeEventListener("pointermove", onMove);
      viewport.removeEventListener("pointerleave", onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [store, viewportRef, axis, length]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={axis === "x" ? "Lineal oben" : "Lineal links"}
      className="absolute z-10"
      title={readOnly ? undefined : "Ziehen, um eine Hilfslinie anzulegen"}
      style={{
        left: axis === "x" ? RULER_SIZE : 0,
        top: axis === "x" ? 0 : RULER_SIZE,
        width: axis === "x" ? length : RULER_SIZE,
        height: axis === "x" ? RULER_SIZE : length,
        cursor: readOnly ? "default" : axis === "x" ? "row-resize" : "col-resize",
        borderRight: axis === "y" ? "1px solid #d4d4d8" : undefined,
      }}
      {...handlers}
      onDoubleClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Guides and layout grid on the canvas (inside the zoomed layer)
// ---------------------------------------------------------------------------

/** Guides and the layout grid, drawn in page coordinates inside the zoomed layer. */
export function LayoutOverlay({
  viewportRef,
  zoom,
  pan,
}: {
  viewportRef: React.RefObject<HTMLDivElement | null>;
  zoom: number;
  pan: { x: number; y: number };
}) {
  const aids = useLayoutAids();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const showGuides = useViewPrefs((s) => s.guides);
  const showGrid = useViewPrefs((s) => s.grid);
  const size = useViewportSize(viewportRef);
  if (!aids) return null;
  // Visible part of the canvas in page coordinates (guides span all of it).
  const visible = {
    x: -pan.x / zoom,
    y: -pan.y / zoom,
    width: size.width / zoom,
    height: size.height / zoom,
  };
  return (
    <>
      {showGrid && doc.layoutGrid ? (
        <LayoutGridOverlay doc={doc} pageIndex={pageIndex} zoom={zoom} />
      ) : null}
      {showGuides
        ? (doc.guides ?? []).map((guide) => (
            <GuideLine
              key={guide.id}
              guide={guide}
              zoom={zoom}
              visible={visible}
              viewportRef={viewportRef}
            />
          ))
        : null}
    </>
  );
}

function LayoutGridOverlay({
  doc,
  pageIndex,
  zoom,
}: {
  doc: SceneDocument;
  pageIndex: number;
  zoom: number;
}) {
  const grid = doc.layoutGrid!;
  const pageDim = pageSize(doc, doc.pages[pageIndex]);
  const geometry = layoutGridGeometry(grid, pageDim.width, pageDim.height);
  const { area } = geometry;
  const line = 1 / zoom;
  return (
    <div className="pointer-events-none absolute" style={{ left: 0, top: 0 }}>
      {geometry.columns.length > 1
        ? geometry.columns.map((band) => (
            <div
              key={`c${band.start}`}
              className="absolute"
              style={{
                left: band.start,
                top: area.y,
                width: band.end - band.start,
                height: area.height,
                background: GRID_FILL,
                boxShadow: `inset ${line}px 0 0 ${GRID_COLOR}, inset -${line}px 0 0 ${GRID_COLOR}`,
              }}
            />
          ))
        : null}
      {geometry.rows.length > 1
        ? geometry.rows.map((band) => (
            <div
              key={`r${band.start}`}
              className="absolute"
              style={{
                left: area.x,
                top: band.start,
                width: area.width,
                height: band.end - band.start,
                background: GRID_FILL,
                boxShadow: `inset 0 ${line}px 0 ${GRID_COLOR}, inset 0 -${line}px 0 ${GRID_COLOR}`,
              }}
            />
          ))
        : null}
      <div
        className="absolute"
        style={{
          left: area.x,
          top: area.y,
          width: area.width,
          height: area.height,
          outline: `${line}px solid ${GRID_COLOR}`,
        }}
      />
    </div>
  );
}

function GuideLine({
  guide,
  zoom,
  visible,
  viewportRef,
}: {
  guide: Guide;
  zoom: number;
  visible: { x: number; y: number; width: number; height: number };
  viewportRef: React.RefObject<HTMLDivElement | null>;
}) {
  const store = useEditorApi();
  const locked = useViewPrefs((s) => s.guidesLocked);
  const readOnly = useEditor((s) => s.readOnly);
  const [hover, setHover] = useState(false);
  const removing = useGuideUi((s) => s.readout?.remove === true && s.readout.id === guide.id);
  const handlers = useGuideDragHandlers(viewportRef, () =>
    locked ? null : { axis: guide.axis, id: guide.id },
  );
  const interactive = !locked && !readOnly;
  const hit = GUIDE_HIT_PX / zoom;
  const vertical = guide.axis === "x";
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: guides are pointer targets; the dialog and the view menu cover the keyboard
    <div
      className="absolute"
      style={{
        ...(vertical
          ? { left: guide.position - hit / 2, top: visible.y, width: hit, height: visible.height }
          : { top: guide.position - hit / 2, left: visible.x, height: hit, width: visible.width }),
        pointerEvents: interactive ? "auto" : "none",
        cursor: vertical ? "col-resize" : "row-resize",
      }}
      title={interactive ? `Hilfslinie ${formatLength(store.getState().doc, guide.position)}` : ""}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      {...handlers}
      onDoubleClick={(event) => {
        event.stopPropagation();
        useGuideUi.setState({ dialog: { id: guide.id } });
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        useGuideUi.setState({ menu: { id: guide.id, x: event.clientX, y: event.clientY } });
      }}
    >
      <div
        className="absolute"
        style={{
          background: GUIDE_COLOR,
          opacity: removing ? 0.3 : hover && interactive ? 1 : 0.8,
          ...(vertical
            ? {
                left: (hit - (hover ? 2 : 1) / zoom) / 2,
                top: 0,
                bottom: 0,
                width: (hover ? 2 : 1) / zoom,
              }
            : {
                top: (hit - (hover ? 2 : 1) / zoom) / 2,
                left: 0,
                right: 0,
                height: (hover ? 2 : 1) / zoom,
              }),
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Menu and dialog of a guide
// ---------------------------------------------------------------------------

/** Context menu and position dialog of the guides (rendered once per editor). */
export function GuideControls() {
  const store = useEditorApi();
  const menu = useGuideUi((s) => s.menu);
  const dialog = useGuideUi((s) => s.dialog);
  const locked = useViewPrefs((s) => s.guidesLocked);
  const close = () => useGuideUi.setState({ menu: null });
  return (
    <>
      {menu ? (
        <DropdownMenu open onOpenChange={(open) => !open && close()} modal={false}>
          <DropdownTrigger asChild>
            <span
              aria-hidden="true"
              style={{ position: "fixed", left: menu.x, top: menu.y, width: 1, height: 1 }}
            />
          </DropdownTrigger>
          <DropdownContent align="start" className="min-w-56">
            <DropdownItem
              onSelect={() => useGuideUi.setState({ menu: null, dialog: { id: menu.id } })}
            >
              <Crosshair /> Position eingeben …
            </DropdownItem>
            <DropdownItem
              onSelect={() => useViewPrefs.getState().setPrefs({ guidesLocked: !locked })}
            >
              {locked ? <LockOpen /> : <Lock />} Hilfslinien {locked ? "entsperren" : "sperren"}
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem danger onSelect={() => removeGuide(store, menu.id)}>
              <Trash2 /> Hilfslinie löschen
            </DropdownItem>
            <DropdownItem danger onSelect={() => clearGuides(store)}>
              <Trash2 /> Alle Hilfslinien löschen
            </DropdownItem>
          </DropdownContent>
        </DropdownMenu>
      ) : null}
      <GuideDialog
        store={store}
        request={dialog}
        onClose={() => useGuideUi.setState({ dialog: null })}
      />
    </>
  );
}

function GuideDialog({
  store,
  request,
  onClose,
}: {
  store: EditorStore;
  request: { id: string } | { axis: Guide["axis"]; position: number } | null;
  onClose: () => void;
}) {
  const doc = useEditor((s) => s.doc);
  const units = layoutUnits(doc);
  const existing =
    request && "id" in request ? doc.guides?.find((guide) => guide.id === request.id) : undefined;
  const [axis, setAxis] = useState<Guide["axis"]>("x");
  const [text, setText] = useState("");
  const open = Boolean(request) && (!request || !("id" in request) || Boolean(existing));
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the dialog opens
  useEffect(() => {
    if (!request) return;
    const start = existing ?? ("axis" in request ? request : null);
    if (!start) return;
    setAxis(start.axis);
    const value = Number(units.toDisplay(start.position).toFixed(units.precision + 1));
    setText(value.toLocaleString("de-DE"));
  }, [request]);
  const parsed = Number(text.replace(",", ".").trim());
  const valid = text.trim() !== "" && Number.isFinite(parsed);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    const position = units.fromDisplay(parsed);
    if (existing) moveGuide(store, existing.id, position);
    else addGuide(store, axis, position);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        title={existing ? "Hilfslinie" : "Neue Hilfslinie"}
        description="Abstand vom Endformat – Nullpunkt ist die obere linke Ecke der Seite."
      >
        <form onSubmit={submit} className="space-y-4">
          {existing ? null : (
            <Segmented
              className="w-full"
              value={axis}
              onChange={setAxis}
              options={[
                { value: "x", label: "Senkrecht", title: "Senkrechte Hilfslinie" },
                { value: "y", label: "Waagerecht", title: "Waagerechte Hilfslinie" },
              ]}
            />
          )}
          <Field
            label={`${axis === "x" ? "Abstand von links" : "Abstand von oben"} (${units.unit})`}
          >
            <Input
              autoFocus
              inputMode="decimal"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </Field>
          <DialogFooter>
            {existing ? (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-red-600"
                onClick={() => {
                  removeGuide(store, existing.id);
                  onClose();
                }}
              >
                <Trash2 /> Löschen
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!valid}>
              {existing ? "Übernehmen" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
